// src/components/charts/WeeklyBarChart.tsx
import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  LabelList,
} from "recharts";

type Props = {
  data: number[]; // 월~일
  maxHint?: number; // 15000
  height?: number; // 255
  barWidth?: number; // 24
  gap?: number; // 14
};

// const DAYS =
//   maxHint <= 100
//     ? ["식단", "운동", "걷기", "복약"]
//     : ["월", "화", "수", "목", "금", "토", "일"];

const WeeklyBarChart: React.FC<Props> = ({
  data,
  maxHint = 15000,
  height = 255,
  barWidth = 24,
  gap = 14,
}) => {
  const rows = useMemo(() => {
    const DAYS =
      maxHint <= 100
        ? ["식단", /*  "운동", */ "걷기", "복약"]
        : ["월", "화", "수", "목", "금", "토", "일"];

    return DAYS.map((day, i) => ({ day, value: data[i] ?? 0 }));
  }, [data, maxHint]);

  const maxVal = useMemo(() => Math.max(maxHint, ...data, 1), [data, maxHint]);
  // const ticks = [maxHint / 3, (maxHint * 2) / 3, maxHint]; // 5k/10k/15k
  const ticks =
    maxHint <= 100
      ? [0, maxHint / 4, maxHint / 2, maxHint / 1.33333, maxHint]
      : [
          maxHint / 5,
          (maxHint * 2) / 5,
          (maxHint * 3) / 5,
          (maxHint * 4) / 5,
          maxHint,
        ]; // 5k/10k/15k

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: `${height}px`,
        height,
        borderRadius: 16,
        overflow: "hidden",
        background: "#fff",
        boxShadow: "0 1px 0 rgba(0,0,0,0.04) inset",
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          barCategoryGap={gap}
          barSize={barWidth}
          margin={{ top: 8, right: 12, left: 12, bottom: 15 }} // ⬅️ 작게 유지
        >
          <CartesianGrid
            horizontal
            vertical={false}
            stroke="#000"
            strokeOpacity={0.18}
            strokeDasharray="2 2"
          />

          {/* X축: 라벨 영역 얇게 + 라벨을 위로 끌어올림 */}
          <XAxis
            xAxisId="labels"
            dataKey="day"
            type="category"
            interval={0}
            allowDuplicatedCategory={false}
            padding={{ left: 0, right: 0 }}
            axisLine={false}
            tickLine={false}
            height={16} // ⬅️ 라벨용 영역 얇게
            tickMargin={1} // ⬅️ 간격 거의 없음
            tick={{ fill: "#9CA3AF", fontSize: 12, dy: -30 }} // ⬅️ 위로 2px
          />

          {/* 세로 경계 전용 숫자축(숨김) + ReferenceLine은 그대로 */}
          <XAxis type="number" domain={[0, 7]} hide xAxisId="edges" />
          {Array.from({ length: 8 }, (_, i) => (
            <ReferenceLine
              key={i}
              x={i}
              xAxisId="edges"
              stroke="#000"
              strokeOpacity={0.18}
              strokeDasharray="2 2"
              ifOverflow="discard"
            />
          ))}

          <YAxis
            orientation={maxHint <= 100 ? "left" : "right"}
            domain={[0, maxVal]}
            ticks={ticks}
            tick={{ fill: "#9CA3AF", fontSize: 12 }}
            tickFormatter={(n) => n.toLocaleString()}
            axisLine={false}
            tickLine={false}
            width={maxHint <= 100 ? 28 : 44}
          />

          <defs>
            <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFA14A" />
              <stop offset="100%" stopColor="#F8932D" />
            </linearGradient>
          </defs>

          <Bar
            xAxisId="labels"
            dataKey="value"
            fill="url(#barGrad)"
            radius={maxHint <= 100 ? [10, 10, 0, 0] : [0, 0, 0, 0]}
            style={{ filter: "drop-shadow(0 8px 18px rgba(248,147,45,0.18))" }}
          >
            {maxHint <= 100 && (
              <LabelList
                dataKey="value"
                position="bottom"
                content={(props) => {
                  const { x, y, width, value, height } = props as any;
                  return (
                    <text
                      x={x! + width! / 2}
                      y={y! + height! - 8}
                      textAnchor="middle"
                      fill={value < 8 ? "#272727" : "#fff8f1"}
                      fontSize={12}
                      fontWeight={700}
                    >
                      {value}%
                    </text>
                  );
                }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default WeeklyBarChart;
